import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useUpdateOrgContractDocument } from '@/graphql';

interface UpdateArchiveOptions {
  itemId: string;
  action: 'archive' | 'unarchive' | 'move-to-trash' | 'restore';
  isArchived?: boolean;
  isInTrash?: boolean;
  successKey: string;
  errorKey: string;
}

export const useContractArchiveActions = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();
  const updateMutation = useUpdateOrgContractDocument();
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null);
  const [updatingAction, setUpdatingAction] = useState<UpdateArchiveOptions['action'] | null>(null);

  const updateDocumentState = async ({
    itemId,
    action,
    isArchived,
    isInTrash,
    successKey,
    errorKey,
  }: UpdateArchiveOptions) => {
    if (updateMutation.isPending) return;

    setUpdatingItemId(itemId);
    setUpdatingAction(action);

    try {
      await updateMutation.mutateAsync({
        ItemId: itemId,
        ...(isArchived !== undefined ? { IsArchived: isArchived } : {}),
        ...(isInTrash !== undefined ? { IsInTrash: isInTrash } : {}),
      });

      toast({
        variant: 'success',
        title: t(successKey),
      });
    } catch (error) {
      handleError(error, { title: errorKey });
    } finally {
      setUpdatingItemId((currentItemId) => (currentItemId === itemId ? null : currentItemId));
      setUpdatingAction((currentAction) => (currentAction === action ? null : currentAction));
    }
  };

  const archiveDocument = async (itemId: string) => {
    await updateDocumentState({
      itemId,
      action: 'archive',
      isArchived: true,
      successKey: 'ORGCONTRACTS_TOAST_ARCHIVE_SUCCESS',
      errorKey: 'ORGCONTRACTS_TOAST_ARCHIVE_ERROR',
    });
  };

  const unarchiveDocument = async (itemId: string) => {
    await updateDocumentState({
      itemId,
      action: 'unarchive',
      isArchived: false,
      successKey: 'ORGARCHIVE_TOAST_UNARCHIVE_SUCCESS',
      errorKey: 'ORGARCHIVE_TOAST_UNARCHIVE_ERROR',
    });
  };

  const moveToTrashDocument = async (itemId: string) => {
    await updateDocumentState({
      itemId,
      action: 'move-to-trash',
      isInTrash: true,
      successKey: 'ORGTRASH_TOAST_MOVE_SUCCESS',
      errorKey: 'ORGTRASH_TOAST_MOVE_ERROR',
    });
  };

  const restoreFromTrashDocument = async (itemId: string) => {
    await updateDocumentState({
      itemId,
      action: 'restore',
      isInTrash: false,
      successKey: 'ORGTRASH_TOAST_RESTORE_SUCCESS',
      errorKey: 'ORGTRASH_TOAST_RESTORE_ERROR',
    });
  };

  return {
    isUpdating: updateMutation.isPending,
    updatingItemId,
    updatingAction,
    archiveDocument,
    unarchiveDocument,
    moveToTrashDocument,
    restoreFromTrashDocument,
  };
};
